import { normalizeAddressRegionCode } from './address-region-options'
import { truncateOrderField } from './order-field-limits'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type { CustomerAddressPlace } from '@/lib/services/customers/customers.address'

const NAMED_DESTINATION_PLACE_TYPES: ReadonlySet<unknown> = new Set([
  'establishment',
  'point_of_interest',
])

type OrderDeliveryAddress = Pick<
  Address,
  'line1' | 'line2' | 'city' | 'state' | 'postal_code' | 'postal_code_suffix'
>

type OrderDeliveryPlace = CustomerAddressPlace & {
  name?: unknown
  types?: unknown
  formattedAddress?: string | null
}

interface LimitedOrderDeliveryAddressFields {
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
}

type LimitedOrderDeliveryAddress = LimitedOrderDeliveryAddressFields &
  Pick<OrderDeliveryAddress, 'postal_code_suffix'>

function normalizedComparableText(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy coercion when comparing place names and address lines
  return String(value ?? '')
    .trim()
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase()
}

function buildPlaceLine1(
  place: OrderDeliveryPlace = {},
  fallbackAddress: Partial<OrderDeliveryAddress> = {}
): string {
  return (
    [place.street_number, place.route].filter(Boolean).join(' ').trim() ||
    place.formattedAddress ||
    fallbackAddress.line1 ||
    ''
  )
}

export function limitOrderDeliveryAddressFields<T extends Partial<OrderDeliveryAddress>>(
  address: T
): Omit<T, keyof LimitedOrderDeliveryAddressFields> & LimitedOrderDeliveryAddressFields
export function limitOrderDeliveryAddressFields(
  address?: undefined
): LimitedOrderDeliveryAddressFields
export function limitOrderDeliveryAddressFields(
  address: Partial<OrderDeliveryAddress> = {}
): LimitedOrderDeliveryAddressFields {
  return {
    ...address,
    line1: truncateOrderField('address_line1', address.line1 ?? ''),
    line2: truncateOrderField('address_line2', address.line2 ?? ''),
    city: truncateOrderField('address_city', address.city ?? ''),
    state: normalizeAddressRegionCode(address.state),
    postal_code: truncateOrderField('address_postal_code', address.postal_code ?? ''),
  }
}

export function buildLimitedOrderDeliveryAddressFields(
  place: OrderDeliveryPlace = {},
  fallbackAddress: Partial<OrderDeliveryAddress> = {}
): LimitedOrderDeliveryAddress {
  return limitOrderDeliveryAddressFields({
    line1: buildPlaceLine1(place, fallbackAddress),
    line2: place.subpremise || fallbackAddress.line2 || '',
    city: place.locality || place.neighborhood || fallbackAddress.city || '',
    state: place.administrative_area_level_1 || fallbackAddress.state,
    postal_code: place.postal_code || fallbackAddress.postal_code || '',
    postal_code_suffix: place.postal_code_suffix || null,
  })
}

export function detectOrderDeliveryDestinationName(place: OrderDeliveryPlace = {}): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve legacy coercion of optional place names
  const name = String(place.name ?? '').trim()
  const types: unknown[] = Array.isArray(place.types) ? place.types : []

  if (!name || !types.some((type) => NAMED_DESTINATION_PLACE_TYPES.has(type))) {
    return ''
  }

  const normalizedName = normalizedComparableText(name)
  const normalizedLine1 = normalizedComparableText(buildPlaceLine1(place))
  const normalizedFormattedLine1 = normalizedComparableText(
    String(place.formattedAddress ?? '').split(',')[0]
  )

  if (
    normalizedName === normalizedLine1 ||
    (normalizedFormattedLine1 && normalizedName === normalizedFormattedLine1)
  ) {
    return ''
  }

  return truncateOrderField('address_name', name)
}

export function buildOrderDeliveryAddressFieldsFromPlace(
  place: OrderDeliveryPlace = {},
  fallbackAddress: Partial<OrderDeliveryAddress> = {}
): LimitedOrderDeliveryAddress & { name: string } {
  return {
    name: detectOrderDeliveryDestinationName(place),
    ...buildLimitedOrderDeliveryAddressFields(place, fallbackAddress),
  }
}
